import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Badge, Loading } from '../components/ui';
import { RegionBottomSheet } from '../components/RegionBottomSheet';
import { CashbackBlocks } from '../components/CashbackBlocks';
import { useSettingsStore, useUserStore } from '../store';
import { useTelegram } from '../hooks/useTelegram';
import { api } from '../services/api';
import { Order, OrderStatus } from '../types';
import { formatPrice, formatDate } from '../utils/helpers';

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { settings } = useSettingsStore();
  const userRegion = useUserStore((s) => s.lastSelectedRegion);
  const { user: tgUser, openTelegramLink } = useTelegram();

  const regionLabels: Record<string, string> = { TR: 'Турция', IN: 'Индия', UA: 'Украина (СНГ)' };
  const regionImages: Record<string, string> = { TR: '/regions/icons/tr.webp', IN: '/regions/icons/in.webp', UA: '/regions/icons/ua.webp' };
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRegionSheet, setShowRegionSheet] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    api.getOrders()
      .then((data) => {
        setOrders(data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      })
      .finally(() => setIsLoading(false));
  }, []);

  const displayName = tgUser?.username
    ? `@${tgUser.username}`
    : tgUser?.first_name || 'Пользователь';

  const initials = (tgUser?.first_name?.[0] || 'П').toUpperCase();

  const handleOpenSupport = () => {
    if (settings.supportUrl) {
      openTelegramLink(settings.supportUrl);
    }
  };

  const getStatusBadge = (status: OrderStatus) => {
    const variants: Record<OrderStatus, { variant: 'success' | 'warning' | 'error' | 'info'; text: string }> = {
      completed: { variant: 'success', text: 'Выполнен' },
      ready: { variant: 'success', text: 'Готов' },
      awaiting_payment: { variant: 'warning', text: 'Ожидает оплаты' },
      pending: { variant: 'warning', text: 'Ожидает' },
      processing: { variant: 'info', text: 'Обработка' },
      manual: { variant: 'warning', text: 'Вручную' },
      failed: { variant: 'error', text: 'Ошибка' },
      cancelled: { variant: 'error', text: 'Отменен' },
    };
    const info = variants[status] || { variant: 'warning' as const, text: status };
    return <Badge variant={info.variant} size="sm">{info.text}</Badge>;
  };

  return (
    <Layout>
      <div
        className="flex flex-col"
        style={{
          background: '#F2F2F7',
        }}
      >
        {/* Gradient Header */}
        <div
          className="relative px-5 pt-6 pb-8 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #0B1A4B 0%, #2840A8 35%, #4361EE 60%, #8AB4F8 100%)' }}
        >
          <div className="flex items-center gap-4">
            {tgUser?.photo_url ? (
              <img
                src={tgUser.photo_url}
                alt={displayName}
                className="rounded-2xl object-cover flex-shrink-0 border-2 border-white/20 shadow-lg"
                style={{ height: 56, width: 56 }}
              />
            ) : (
              <div
                className="rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border-2 border-white/20"
                style={{ height: 56, width: 56 }}
              >
                <span className="text-white text-xl font-bold">{initials}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-white leading-tight truncate">
                {displayName}
              </h1>
              <p className="text-white/60 text-xs mt-0.5">XPlay Store</p>
            </div>
          </div>
        </div>

        {/* Content with rounded top overlapping gradient */}
        <div className="-mt-5 relative z-10">
          <div className="bg-background rounded-t-[20px] px-4 pt-4 pb-2">
          {/* Бонусный баланс + реферальная программа */}
          <CashbackBlocks />

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => navigate('/orders')}
              className="bg-white rounded-2xl border border-gray-200/80 p-4 flex flex-col items-center gap-4 active:scale-[0.97] transition-transform"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M14.25 2.5C14.25 2.4337 14.2237 2.37011 14.1768 2.32322C14.1299 2.27634 14.0663 2.25 14 2.25H7C6.27065 2.25 5.57118 2.53973 5.05546 3.05546C4.53973 3.57118 4.25 4.27065 4.25 5V19C4.25 19.7293 4.53973 20.4288 5.05546 20.9445C5.57118 21.4603 6.27065 21.75 7 21.75H17C17.7293 21.75 18.4288 21.4603 18.9445 20.9445C19.4603 20.4288 19.75 19.7293 19.75 19V9.147C19.75 9.0807 19.7237 9.01711 19.6768 8.97022C19.6299 8.92334 19.5663 8.897 19.5 8.897H15C14.8011 8.897 14.6103 8.81798 14.4697 8.67733C14.329 8.53668 14.25 8.34591 14.25 8.147V2.5ZM15 12.25C15.1989 12.25 15.3897 12.329 15.5303 12.4697C15.671 12.6103 15.75 12.8011 15.75 13C15.75 13.1989 15.671 13.3897 15.5303 13.5303C15.3897 13.671 15.1989 13.75 15 13.75H9C8.80109 13.75 8.61032 13.671 8.46967 13.5303C8.32902 13.3897 8.25 13.1989 8.25 13C8.25 12.8011 8.32902 12.6103 8.46967 12.4697C8.61032 12.329 8.80109 12.25 9 12.25H15ZM15 16.25C15.1989 16.25 15.3897 16.329 15.5303 16.4697C15.671 16.6103 15.75 16.8011 15.75 17C15.75 17.1989 15.671 17.3897 15.5303 17.5303C15.3897 17.671 15.1989 17.75 15 17.75H9C8.80109 17.75 8.61032 17.671 8.46967 17.5303C8.32902 17.3897 8.25 17.1989 8.25 17C8.25 16.8011 8.32902 16.6103 8.46967 16.4697C8.61032 16.329 8.80109 16.25 9 16.25H15Z" fill="#3657F0"/>
                <path d="M15.75 2.82396C15.75 2.63996 15.943 2.52296 16.086 2.63796C16.2073 2.73596 16.315 2.84996 16.409 2.97996L19.422 7.17696C19.49 7.27296 19.416 7.39696 19.298 7.39696H16C15.9337 7.39696 15.8701 7.37062 15.8232 7.32373C15.7763 7.27685 15.75 7.21326 15.75 7.14696V2.82396Z" fill="#3657F0"/>
              </svg>
              <span className="text-[13px] font-semibold text-text-primary">Заказы</span>
            </button>
            <button
              onClick={handleOpenSupport}
              className="bg-white rounded-2xl border border-gray-200/80 p-4 flex flex-col items-center gap-4 active:scale-[0.97] transition-transform"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.2994 8.51251C19.8477 7.43679 19.1906 6.45903 18.3647 5.63372C17.5413 4.80699 16.5632 4.14968 15.4859 3.69913C14.3823 3.23532 13.1965 2.99758 11.9991 3.00002H11.9588C10.7408 3.00605 9.56312 3.24712 8.45386 3.72122C7.38582 4.1764 6.41688 4.83488 5.6012 5.65984C4.78331 6.48328 4.13374 7.45763 3.68868 8.52858C3.2267 9.64226 2.9926 10.8369 3.00018 12.0422C3.00622 13.4364 3.3404 14.8205 3.96449 16.058V19.1116C3.96449 19.6219 4.3792 20.0357 4.88853 20.0357H7.94453C9.19042 20.6631 10.5653 20.9932 11.9608 21H12.0031C13.207 21 14.3726 20.767 15.4718 20.3109C16.5436 19.8658 17.5181 19.2161 18.3406 18.3984C19.17 17.5768 19.8222 16.6165 20.2792 15.5458C20.7523 14.4368 20.9939 13.2576 21 12.0402C21.004 10.8168 20.7664 9.62948 20.2994 8.51251ZM7.98077 12.9643C7.44929 12.9643 7.01646 12.5324 7.01646 12C7.01646 11.4676 7.44929 11.0357 7.98077 11.0357C8.51224 11.0357 8.94508 11.4676 8.94508 12C8.94508 12.5324 8.51426 12.9643 7.98077 12.9643ZM11.9991 12.9643C11.4676 12.9643 11.0347 12.5324 11.0347 12C11.0347 11.4676 11.4676 11.0357 11.9991 11.0357C12.5305 11.0357 12.9634 11.4676 12.9634 12C12.9634 12.5324 12.5305 12.9643 11.9991 12.9643ZM16.0173 12.9643C15.4859 12.9643 15.053 12.5324 15.053 12C15.053 11.4676 15.4859 11.0357 16.0173 11.0357C16.5488 11.0357 16.9817 11.4676 16.9817 12C16.9817 12.5324 16.5488 12.9643 16.0173 12.9643Z" fill="#3657F0"/>
              </svg>
              <span className="text-[13px] font-semibold text-text-primary">Поддержка</span>
            </button>
          </div>

          {/* Region selector */}
          <button
            onClick={() => setShowRegionSheet(true)}
            className="w-full bg-white rounded-2xl border border-gray-200/80 p-4 flex items-center justify-between mb-3 active:scale-[0.99] transition-transform"
          >
            <div className="flex items-center gap-3">
              {userRegion && regionImages[userRegion] ? (
                <div className="w-16 h-16 rounded-2xl overflow-hidden flex-shrink-0 border border-gray-200/80">
                  <img
                    src={regionImages[userRegion]}
                    alt={regionLabels[userRegion]}
                    className="w-full h-full object-cover"
                    decoding="async"
                  />
                </div>
              ) : userRegion === 'UA' ? (
                <div
                  className="w-16 h-16 rounded-2xl flex-shrink-0 border border-gray-200/80"
                  style={{ background: 'linear-gradient(135deg, #5370F5 0%, #3657F0 100%)' }}
                />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex-shrink-0 flex items-center justify-center text-[32px]">🌍</div>
              )}
              <div className="text-left">
                <div className="text-[13px] text-text-secondary">Регион PlayStation</div>
                <div className="text-[17px] font-semibold text-text-primary">
                  {userRegion ? regionLabels[userRegion] : 'Не выбран'}
                </div>
              </div>
            </div>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="#BBBEC0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Recent Orders */}
          <div className="bg-white p-4 flex flex-col gap-4" style={{ borderRadius: '16px', border: '1px solid #E1E3E5' }}>
            <h2 className="text-[15px] font-bold text-text-primary">Последние заказы</h2>
            {isLoading ? (
              <Loading />
            ) : orders.length > 0 ? (
              <div className="flex flex-col gap-3">
                {orders.slice(0, 3).map((order) => {
                  // Steam-заказы (в т.ч. старые, без items) — с лого Steam
                  const isSteamTopup = !!order.steamLogin || order.orderNumber.startsWith('ST-');
                  const cover = isSteamTopup ? '/services/logo-steam.webp' : order.items[0]?.product.coverImage;
                  return (
                  <button
                    key={order.id}
                    onClick={() => navigate(`/order-status/${order.id}`)}
                    className="w-full flex items-center gap-3 active:opacity-80 transition-opacity text-left"
                    style={{ height: '44px', borderRadius: '12px', padding: '12px' }}
                  >
                    {cover && (
                      <img
                        src={cover}
                        alt=""
                        className="rounded-lg object-cover flex-shrink-0"
                        style={{ height: 44, width: 44 }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-text-primary">{order.orderNumber}</span>
                        {getStatusBadge(order.status)}
                      </div>
                      <p className="text-xs text-text-tertiary mt-0.5">
                        {formatDate(order.createdAt)} · {formatPrice(order.totalAmount)}
                      </p>
                    </div>
                  </button>
                  );
                })}
                {orders.length > 3 && (
                  <button onClick={() => navigate('/orders')} className="text-xs font-bold text-primary self-center">
                    Все заказы →
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center py-2">
                <div className="flex items-center justify-center mb-3" style={{ width: '80px', height: '80px', borderRadius: '16px', background: '#F4F4F7' }}>
                  <svg width="20" height="24" viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12.3077 0.307692C12.3077 0.226087 12.2753 0.147825 12.2176 0.0901211C12.1599 0.0324176 12.0816 0 12 0H3.38462C2.48696 0 1.62607 0.356592 0.991331 0.991331C0.356592 1.62607 0 2.48696 0 3.38462V20.6154C0 21.513 0.356592 22.3739 0.991331 23.0087C1.62607 23.6434 2.48696 24 3.38462 24H15.6923C16.59 24 17.4509 23.6434 18.0856 23.0087C18.7203 22.3739 19.0769 21.513 19.0769 20.6154V8.48862C19.0769 8.40701 19.0445 8.32875 18.9868 8.27104C18.9291 8.21334 18.8508 8.18092 18.7692 8.18092H13.2308C12.986 8.18092 12.7512 8.08367 12.5781 7.91056C12.4049 7.73745 12.3077 7.50266 12.3077 7.25785V0.307692ZM13.2308 12.3077C13.4756 12.3077 13.7104 12.4049 13.8835 12.5781C14.0566 12.7512 14.1538 12.986 14.1538 13.2308C14.1538 13.4756 14.0566 13.7104 13.8835 13.8835C13.7104 14.0566 13.4756 14.1538 13.2308 14.1538H5.84615C5.60134 14.1538 5.36655 14.0566 5.19344 13.8835C5.02033 13.7104 4.92308 13.4756 4.92308 13.2308C4.92308 12.986 5.02033 12.7512 5.19344 12.5781C5.36655 12.4049 5.60134 12.3077 5.84615 12.3077H13.2308ZM13.2308 17.2308C13.4756 17.2308 13.7104 17.328 13.8835 17.5011C14.0566 17.6742 14.1538 17.909 14.1538 18.1538C14.1538 18.3987 14.0566 18.6335 13.8835 18.8066C13.7104 18.9797 13.4756 19.0769 13.2308 19.0769H5.84615C5.60134 19.0769 5.36655 18.9797 5.19344 18.8066C5.02033 18.6335 4.92308 18.3987 4.92308 18.1538C4.92308 17.909 5.02033 17.6742 5.19344 17.5011C5.36655 17.328 5.60134 17.2308 5.84615 17.2308H13.2308Z" fill="#818384"/>
                    <path d="M14.1538 0.706521C14.1538 0.480059 14.3913 0.336059 14.5673 0.477598C14.7167 0.598213 14.8492 0.738521 14.9649 0.898521L18.6732 6.06406C18.7569 6.18221 18.6658 6.33483 18.5206 6.33483H14.4615C14.3799 6.33483 14.3016 6.30241 14.2439 6.24471C14.1862 6.187 14.1538 6.10874 14.1538 6.02714V0.706521Z" fill="#818384"/>
                  </svg>
                </div>
                <p className="text-sm text-text-tertiary mb-3">Заказов пока нет</p>
                <button
                  onClick={() => navigate('/home')}
                  className="text-white text-sm font-bold active:scale-[0.97] transition-transform"
                  style={{
                    width: '240px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'linear-gradient(90deg, #5370F5, #3657F0, #5370F5)',
                  }}
                >
                  Перейти в каталог
                </button>
              </div>
            )}
          </div>

          {/* Legal Links */}
          <div className="bg-white p-4 flex flex-col mt-3" style={{ borderRadius: '16px', border: '1px solid #E1E3E5' }}>
            <button
              onClick={() => navigate('/privacy')}
              className="w-full flex items-center justify-between active:opacity-80 transition-opacity"
              style={{ height: '40px' }}
            >
              <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '15px', fontWeight: 400, lineHeight: '20px', color: '#1A1D1A' }}>Политика конфиденциальности</span>
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#F4F4F7' }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 4.5L13 10L7.5 15.5" stroke="#818384" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </button>
            <div style={{ height: '1px', background: '#E1E3E5', margin: '8px 0' }} />
            <button
              onClick={() => navigate('/terms')}
              className="w-full flex items-center justify-between active:opacity-80 transition-opacity"
              style={{ height: '40px' }}
            >
              <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '15px', fontWeight: 400, lineHeight: '20px', color: '#1A1D1A' }}>Условия использования</span>
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#F4F4F7' }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 4.5L13 10L7.5 15.5" stroke="#818384" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </button>
          </div>
          </div>
        </div>
      </div>
      <RegionBottomSheet isOpen={showRegionSheet} onClose={() => setShowRegionSheet(false)} />
    </Layout>
  );
};
